'use client'
import { Avatar, Popover, Modal } from 'antd'
import { Suspense, useEffect, useState } from 'react'
import Link from 'next/link'
import LifecycleLink from '@/components/common/LifecycleLink'
import { twMerge } from 'tailwind-merge'
import Search from './Search'
import Login from './Login'
import Logout from './Logout'
import UserInfo from './UserInfo'
import { useRecoilValue } from 'recoil'
import { userState } from 'Store/user'
import { DefaultAvatar } from '../svg/DefaultAvatar'
import { Logo } from 'Comps/svg/Logo'
import { NavDiscordIcon } from 'Comps/svg/NavDiscordIcon'
import { tipsy_discord } from '@/constant/contact'
import { transformImage } from '@/utils/img'
import { SMALL_IMG_WIDTH } from '@/constant/img'
import { APP_HOME_PATHNAME } from 'Types/common'
import DownloadAppPopover from 'Comps/modal/DownloadAppPopover'
import SubscribeButton from '../subscribe/SubscribeButton'
import GemsButton from '../balance/GemsButton'
import CoinsButton from '../balance/CoinsButton'
import { isAngelUser } from '@/utils/admin'
import { BetaSupporterAvatarFrame } from 'Comps/svg/BetaSupporterAvatarFrame'
import useI18n from 'Hooks/useI18n'
import LanguageSelect from './LanguageSelect'
import { ChristmasAvatarFrame } from 'Comps/svg/ChristmasAvatarFrame'
import {
  CHRISTMAS_DEADLINE_TIME,
  HALLOWEEN_2025_DEADLINE_TIME,
  CHRISTMAS_2025_DEADLINE_TIME,
} from '@/constant/date'
import useCountdown from '@/hooks/useCountdown'
import AnnualPlanBanner from '../subscribe/AnnualPlanBanner'
import FollowUsPopover from '../modal/FollowUsPopover'
import { withLocale } from '@/locales'
import clsx from 'clsx'
import Letter from './Letter'
import DownloadQRModal from '../modal/DownloadQRModal'
import Halloween2025AvatarFrame from '../svg/Halloween2025AvatarFrame'
import Christmas2025AvatarFrame from '../svg/Christmas2025AvatarFrame'
import LinkAccountModal from '../modal/LinkAccountModal'
import useUserLocation from '@/hooks/useUserLocation'
import FantacyStudioEntryCard from '@/components/common/FantacyStudioEntryCard'

type HeaderProps = {
  className?: string
}
export default function Header({ className }: HeaderProps) {
  const { t, lang } = useI18n()
  const realUser = useRecoilValue(userState)
  const [mounted, setMounted] = useState(false)
  const user = mounted ? realUser : null
  const [logoutVisible, setLogoutVisible] = useState(false)
  const [showUserCard, setShowUserCard] = useState(false)
  const { isExpired } = useCountdown(CHRISTMAS_DEADLINE_TIME)
  const { isExpired: isChristmas2025Expired } = useCountdown(
    CHRISTMAS_2025_DEADLINE_TIME
  )
  const { isExpired: isHalloweenExpired } = useCountdown(
    HALLOWEEN_2025_DEADLINE_TIME
  )
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleUserCardOpenChange = (newOpen: boolean) => {
    setShowUserCard(newOpen)
  }
  const { country: userCountry } = useUserLocation()

  return (
    <header
      className={twMerge(
        clsx(
          'pointer-events-none fixed left-0 right-0 top-0 mx-auto flex h-14 max-w-[3840px] select-none items-center justify-between px-[30px]',
          isSearchOpen ? 'z-50' : 'z-10'
        ),
        className
      )}
    >
      <div
        className={clsx('flex min-w-0 items-center', { grow: !isSearchOpen })}
      >
        <LifecycleLink
          href={withLocale(APP_HOME_PATHNAME, lang)}
          aria-label={/* i18n-ignore */ 'Tipsy'}
          prefetch
          scroll={false}
          className="pointer-events-auto"
        >
          <Logo className="h-9" />
        </LifecycleLink>
        {!isSearchOpen && (
          <>
            <div className="ml-2.5 flex h-9 items-center justify-center rounded-full border border-white/40 bg-secondary-bg">
              <div className="pointer-events-auto flex h-full items-center">
                <Link href={tipsy_discord} target="_blank">
                  <NavDiscordIcon size={24} className="mx-1.5" />
                </Link>
                <div className="h-3 w-[1px] bg-white/50" />
                <div className="mx-1.5">
                  <FollowUsPopover />
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowDownloadModal(true)}
              className="pointer-events-auto ml-2.5 flex h-9 items-center gap-1 whitespace-nowrap rounded-full border border-white/40 bg-black/20 px-4 text-sm text-white hover:bg-black/30"
            >
              <img
                src="/app_download.png"
                alt=""
                className="h-5 w-5 shrink-0"
              />
              <span>{t('Download')}</span>
            </button>
            <AnnualPlanBanner />
          </>
        )}
      </div>
      <div className="relative flex flex-1 justify-end gap-1.5 whitespace-nowrap pl-3">
        <Search onOpenChange={(open) => setIsSearchOpen(open)} />
        {user && <SubscribeButton />}
        {user && user.basic_rules_completed && <GemsButton />}
        {user && user.basic_rules_completed && <CoinsButton />}
        <FantacyStudioEntryCard variant="inline" />
        <LifecycleLink
          href={withLocale('/create', lang)}
          prefetch
          className="pointer-events-auto"
        >
          <button className="h-9 cursor-pointer rounded-full border border-secondary-border bg-secondary-bg px-4 py-1 text-sm transition hover:border-hover-border">
            {t('Create')}
          </button>
        </LifecycleLink>
        <LanguageSelect />
        {user && <Letter />}

        {!mounted && (
          // 鉴权状态（来自 localStorage）在 hydration 后才可知，先用同尺寸占位符把头像槽位占住，
          // 避免刷新时右上角头像从无到有撑开导致的布局抖动。
          <div
            aria-hidden
            className="pointer-events-none"
            style={{ display: 'inline-block', width: 40, height: 40 }}
          >
            <Avatar
              alt=""
              style={{ backgroundColor: 'rgba(0,0,0,0)' }}
              size={40}
              icon={<DefaultAvatar />}
            />
          </div>
        )}
        {mounted && user && (
          <Popover
            open={showUserCard}
            onOpenChange={handleUserCardOpenChange}
            trigger="hover"
            overlayInnerStyle={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(87,47,102,0.6) 50%, rgba(29,21,21,0.6) 100%)',
              border: '1px solid rgba(255,255,255,0.6)',
              padding: '0',
              borderRadius: '10px',
            }}
            placement="bottomRight"
            content={
              <UserInfo
                onClose={() => setShowUserCard(false)}
                showLogout={() => setLogoutVisible(true)}
              />
            }
            arrow={false}
            className="max-md:hidden"
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                src={transformImage(user.avatar_url, SMALL_IMG_WIDTH)}
                alt={t('User')}
                style={{
                  backgroundColor: 'rgba(0,0,0,0)',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                }}
                size={40}
                icon={<DefaultAvatar />}
              />
              {isAngelUser(user.user_id) &&
                isExpired &&
                isHalloweenExpired &&
                isChristmas2025Expired && (
                  <BetaSupporterAvatarFrame
                    idPrefix="header-avatar"
                    size={45}
                    style={{
                      position: 'absolute',
                      bottom: '-5px',
                      right: '-3px',
                    }}
                  />
                )}
              {!isExpired && (
                <ChristmasAvatarFrame
                  idPrefix="header-avatar"
                  size="small"
                  style={{ position: 'absolute', top: '-5px', right: '-3px' }}
                />
              )}
              {!isHalloweenExpired && (
                <div
                  style={{ position: 'absolute', top: '-80px', right: '-80px' }}
                >
                  <Halloween2025AvatarFrame size="super-small" />
                </div>
              )}
              {!isChristmas2025Expired && (
                <Christmas2025AvatarFrame
                  avatarSize={40}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </div>
          </Popover>
        )}
        {mounted && !user && (
          <Suspense>
            <Login />
          </Suspense>
        )}
        <Logout
          visible={logoutVisible}
          onClose={() => setLogoutVisible(false)}
        />
      </div>
      <Modal
        open={showDownloadModal}
        onCancel={() => setShowDownloadModal(false)}
        footer={null}
        width={850}
        centered
        closable={false}
        maskClosable={true}
        className="download-app-modal"
        styles={{
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
          content: {
            background:
              'linear-gradient(180deg, #180E1F 0%, #41234D 50%, #1D1515 100%)',
            backgroundColor: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '10px',
            opacity: '0.9',
          },
        }}
      >
        <DownloadQRModal
          country={userCountry || 'US'} // 默认国家为 US，如果 userCountry 还未加载出来
        />
      </Modal>
      <LinkAccountModal />
    </header>
  )
}
